import * as React from 'react';
import styles from './list-view-column-container.scss';
import { ComplexValue, ElementLoader } from 'ui';
import { Component } from 'react';
import { ListViewColumn } from 'app/area/list-view-code-selector/list-view-column';
import { ListViewFolder } from 'app/area/list-view-code-selector/list-view-folder';
import { ListViewItem } from 'app/area/list-view-code-selector/list-view-item';
import { columnWidth } from 'app/area/list-view-code-selector/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

export interface ListViewColumnContainerProps {
  breadcrumbs: ComplexValue<number>[];
  options: ComplexValue<number>[];
  selectFolder: (breadcrumbs: ComplexValue<number>[]) => void;
  selectItem: (breadcrumbs: number[]) => void;
  displaySingleFlexibleColumn?: boolean;
}

export interface ListViewColumnData {
  breadcrumbs: ComplexValue<number>[];
  options: ComplexValue<number>[];
}

export interface ListViewColumnContainerState {
  canRenderColumns: boolean;
}

export class ListViewColumnContainer extends Component<ListViewColumnContainerProps, ListViewColumnContainerState> {
  private columnsAmountSourceRef: React.RefObject<HTMLDivElement | null> = React.createRef();

  public state: ListViewColumnContainerState = {
    canRenderColumns: false
  };

  public componentDidMount() {
    this.setState({
      canRenderColumns: true
    });
  }

  private getAllColumns = (): ListViewColumnData[] => {
    const { breadcrumbs: breadcrumbsRaw, options } = this.props;

    let breadcrumbs = breadcrumbsRaw;

    const initialColumnData = [{ breadcrumbs: [], options }];

    if (!breadcrumbs || breadcrumbs && breadcrumbs.length === 0) {
      return initialColumnData;
    }

    if (breadcrumbs[0] && breadcrumbs[0].value === 0) {
      breadcrumbs = breadcrumbs.splice(1, breadcrumbs.length);
    }

    return breadcrumbs.reduce((columns, breadcrumbItem) => [
      ...columns,
      {
        options: breadcrumbItem.descendants,
        breadcrumbs: [...columns[columns.length - 1].breadcrumbs, breadcrumbItem]
      }
    ], initialColumnData as ListViewColumnData[]);
  };

  private getVisibleColumns = (columns: ListViewColumnData[]): ListViewColumnData[] => {
    if (!columns || (columns && columns.length === 0)) {
      return [];
    }

    let maximumVisibleColumsCount = this.maximumVisibleColumsCount();

    if (!isDefinedIncludingZero(maximumVisibleColumsCount)) {
      return [];
    }

    if (maximumVisibleColumsCount === 0) {
      maximumVisibleColumsCount = 1;
    }

    if (columns.length <= maximumVisibleColumsCount) {
      return columns;
    }

    return columns.splice(columns.length - maximumVisibleColumsCount, columns.length);
  };

  private maximumVisibleColumsCount = (): number => this.columnsAmountSourceRef && this.columnsAmountSourceRef.current
    ? Math.floor(this.columnsAmountSourceRef.current.offsetWidth / columnWidth) : undefined;

  private navigateToFolder = (breadcrumbs: ComplexValue<number>[]) => this.props.selectFolder(breadcrumbs);

  private isOptionInBreadcrumbs = (option: ComplexValue<number>) =>
    this.props.breadcrumbs ? this.props.breadcrumbs.find(breadcrumbItem => breadcrumbItem.value === option.value) !== undefined : false;

  private sortByFolder = (options: ComplexValue<number>[]) =>
    [...options.filter(this.isFolder), ...options.filter(option => !this.isFolder(option))];

  private isFolder = (option: ComplexValue<number>) => option ? option.descendants && option.descendants.length > 0 : false;

  public render() {
    const { selectItem, displaySingleFlexibleColumn = false } = this.props;
    const { canRenderColumns } = this.state;

    const allColumns = canRenderColumns && this.getAllColumns();
    const visibleColumns = canRenderColumns && this.getVisibleColumns(allColumns);

    const listViewColumnContainerClassName = `${styles.listViewColumnContainer} ${styles.listViewColumnContainerMobile}
    ${displaySingleFlexibleColumn ? styles.listViewColumnContainerSingleColumn : ''}`;

    return (
      <div ref={this.columnsAmountSourceRef}>
        <div id="list-view-column-container" className={listViewColumnContainerClassName}>
          {canRenderColumns ?
            <div className={`${styles.listViewColumnContainerContent} ${styles.listViewColumnContainerContentMobile}`}>
              {visibleColumns.map((column, index) =>
                <ListViewColumn key={`list-view-column-${index}`} displaySingleFlexibleColumn={displaySingleFlexibleColumn}>
                  {
                    column && column.options ? this.sortByFolder(column.options).map((option, optionIndex) =>
                      option.descendants ?
                        <ListViewFolder
                          key={`list-view-folder-${option.description}-${optionIndex}`}
                          isActive={this.isOptionInBreadcrumbs(option)}
                          option={option}
                          breadcrumbs={column.breadcrumbs}
                          onClick={this.navigateToFolder} />
                        : <ListViewItem
                          key={`list-view-item-${option.description}-${optionIndex}`}
                          option={option}
                          breadcrumbs={column.breadcrumbs}
                          onClick={selectItem}
                          displayFlexibleItemName={displaySingleFlexibleColumn} />
                    ) : []
                  }
                </ListViewColumn>)}
            </div>
            : <ElementLoader />
          }
        </div>
      </div>
    );
  }
}
